import React, { useId } from "react";
import { Box, Paper, Collapse, Divider } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { motion, transition } from "../config/theme";

export interface MobileCardProps {
  /** Color used for the left accent border. */
  accentColor: string;
  /** Clickable header content (position badge, name, etc.). */
  header: React.ReactNode;
  /** Summary stats row shown below the header. */
  summary: React.ReactNode;
  /** Whether the card is expanded. */
  expanded: boolean;
  /** Called when the header is clicked. */
  onToggle: () => void;
  /** Content revealed when the card is expanded. */
  expandedContent?: React.ReactNode;
}

export function MobileCard({
  accentColor,
  header,
  summary,
  expanded,
  onToggle,
  expandedContent,
}: MobileCardProps) {
  const expandedContentId = useId();
  const isExpandable = expandedContent !== undefined;

  const headerContent = (
    <>
      {header}
      <ChevronRightIcon
        sx={{
          fontSize: 18,
          color: "text.disabled",
          transform: expanded ? "rotate(90deg)" : "none",
          transition: transition(["transform"]),
          flexShrink: 0,
        }}
      />
    </>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        borderLeft: `4px solid ${accentColor}`,
        mb: 1,
        transition: transition(["box-shadow"], motion.fast),
        "&:hover": { boxShadow: 3 },
      }}
    >
      {/* Clickable header row */}
      {isExpandable ? (
        <Box
          component="button"
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={expandedContentId}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            px: 2,
            py: 1.5,
            cursor: "pointer",
            gap: 1,
            userSelect: "none",
            border: 0,
            bgcolor: "transparent",
            font: "inherit",
            textAlign: "left",
            touchAction: "manipulation",
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: -2,
            },
          }}
        >
          {headerContent}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            px: 2,
            py: 1.5,
            gap: 1,
            userSelect: "none",
          }}
        >
          {headerContent}
        </Box>
      )}

      {/* Summary stats row */}
      <Box
        sx={{
          display: "flex",
          px: 2,
          pb: 1.25,
          gap: 2.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        {summary}
      </Box>

      {/* Expanded content */}
      {expandedContent !== undefined && (
        <Collapse id={expandedContentId} in={expanded} timeout={250} unmountOnExit>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>{expandedContent}</Box>
        </Collapse>
      )}
    </Paper>
  );
}
